import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import ConfirmDialog from "@/components/ConfirmDialog";
import api, { formatDetail } from "@/lib/api";

/**
 * LegacyUsersBanner — muncul kalau ada user tanpa `sub_division`.
 * Root cause bug production 149-k (crash CreateTaskDialog). Menampilkan daftar user
 * yang perlu di-backfill + tombol "Backfill Semua" (auto-set ke sub-divisi pertama
 * yang match divisinya di `sub_divisions` collection). Gated matrix `user_management`.
 */
export default function LegacyUsersBanner({ onDone }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users/missing-subdivision");
      setUsers(data || []);
    } catch { /* silent — banner hilang saja kalau tak punya akses */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const backfill = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users/backfill-subdivisions", {});
      toast.success(`${data.updated} user di-backfill${data.skipped_count ? `, ${data.skipped_count} di-skip` : ""}`);
      if (data.skipped_count) {
        // Tampilkan alasan skip biar admin bisa manual fix
        data.skipped.forEach((s) => toast.error(`User ${s.user_id.slice(0, 8)}...: ${s.reason}`, { duration: 8000 }));
      }
      setConfirm(false);
      await load();
      onDone?.();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Backfill gagal"); }
    finally { setBusy(false); }
  };

  if (loading || users.length === 0) return null;

  return (
    <div className="bg-amber-50 border border-amber-300 border-l-4 border-l-amber-500 mb-4" data-testid="legacy-users-banner">
      <div className="px-4 py-3 flex items-center gap-3 flex-wrap">
        <AlertTriangle size={16} className="text-amber-600 shrink-0" />
        <div className="flex-1 min-w-[200px]">
          <div className="text-xs font-semibold text-amber-900">
            {users.length} pegawai tanpa Sub-Division
          </div>
          <div className="text-[11px] text-amber-800 mt-0.5">
            Data legacy — perlu di-backfill supaya <b>Create Task</b> tidak crash (Radix Select requires non-empty value). Klik "Backfill Semua" untuk auto-set ke sub-divisi pertama yang match divisinya.
          </div>
        </div>
        <Button
          type="button"
          data-testid="btn-backfill-subs"
          onClick={() => setConfirm(true)}
          disabled={busy}
          className="rounded-none h-8 text-[10px] uppercase tracking-wider bg-amber-600 hover:bg-amber-700 text-white inline-flex items-center gap-1 disabled:opacity-40"
        ><RotateCcw size={11} /> {busy ? "Memproses…" : "Backfill Semua"}</Button>
      </div>
      <div className="border-t border-amber-200 max-h-40 overflow-y-auto">
        <table className="w-full text-[11px]">
          <thead className="bg-amber-100 text-[10px] uppercase tracking-wider text-amber-900">
            <tr>
              <th className="text-left px-3 py-1">Nama</th>
              <th className="text-left px-3 py-1">Divisi</th>
              <th className="text-left px-3 py-1">Level</th>
              <th className="text-left px-3 py-1">Saran Sub-Division</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-amber-200/60">
                <td className="px-3 py-1 font-semibold text-[#0B1D3A]">{u.name}</td>
                <td className="px-3 py-1">{u.division || <span className="italic text-rose-600">(kosong)</span>}</td>
                <td className="px-3 py-1">Lv{u.level}</td>
                <td className="px-3 py-1">
                  {u.suggested_sub_division ? (
                    <span className="font-mono bg-emerald-100 text-emerald-800 px-1.5 py-0.5">{u.suggested_sub_division}</span>
                  ) : (
                    <span className="italic text-rose-600">Belum ada sub-div untuk divisi ini — tambah dulu di tab Divisions</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title="Backfill Sub-Division Semua User Legacy"
        description={`Sistem akan set otomatis \`sub_division\` untuk ${users.length} user tanpa sub-div ke sub-divisi pertama yang match divisinya. User yang divisinya belum punya sub-div sama sekali akan di-skip (Anda perlu tambah dulu sub-div di tab Divisions).\n\nProses ini idempotent — bisa di-run ulang.`}
        confirmLabel={`Backfill ${users.length} User`}
        onConfirm={backfill}
      />
    </div>
  );
}
